import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  AlertCircle, Pencil, TrendingDown, TrendingUp,
} from 'lucide-react';
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RUPEE_ICON = '/icons/cost-rupee.png';
const DEFAULT_RATE = 13.70;

// ────────────────────────────── helpers ──────────────────────────────
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('radar_session_token')}` });

const lastMondayMidnight = () => {
  const now = new Date();
  const daysSinceMon = (now.getDay() + 6) % 7; // Mon=0 ... Sun=6
  const d = new Date(now);
  d.setDate(now.getDate() - daysSinceMon);
  d.setHours(0, 0, 0, 0);
  return d;
};

const formatINR = (n) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(n || 0));

const compactNumber = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1_00_00_000) return `${(n / 1_00_00_000).toFixed(1)}cr`;
  if (v >= 1_00_000)    return `${(n / 1_00_000).toFixed(1)}L`;
  if (v >= 1_000)       return `${(n / 1_000).toFixed(1)}k`;
  return Math.round(n).toString();
};

const emailLocalpart = (email) => (email && email.includes('@') ? email.split('@')[0] : email || 'you');

// ────────────────────────────── sparkline ──────────────────────────────
function Sparkline({ points }) {
  // points: [{credits}]. Renders a tiny 56×18 polyline.
  if (!points || points.length < 2) return null;
  const W = 56, H = 18, pad = 2;
  const values = points.map(p => p.credits);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const coords = points.map((p, i) => {
    const x = pad + (i * (W - pad * 2)) / (points.length - 1);
    const y = H - pad - ((p.credits - min) / range) * (H - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const last = coords[coords.length - 1].split(',').map(parseFloat);
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <polyline
        fill="none"
        stroke="#2EB4AC"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={coords.join(' ')}
      />
      <circle cx={last[0]} cy={last[1]} r="1.8" fill="#2EB4AC" />
    </svg>
  );
}

// ────────────────────────────── main widget ──────────────────────────────
export default function CostTrackerWidget() {
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const pillRef = useRef(null);

  // Form state
  const [credits, setCredits] = useState('');
  const [rate, setRate] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/cost-tracker`, { headers: authHeader() }),
        axios.get(`${API}/cost-tracker/history?weeks=12`, { headers: authHeader() }),
      ]);
      setState(s.data);
      setHistory(h.data.history || []);
    } catch (e) {
      // Silent — widget is discreet; don't surface network noise.
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('radar_session_token')) return;
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
  }, [fetchAll]);

  // Collapse on outside click
  useEffect(() => {
    if (!expanded) return;
    const onDown = (e) => {
      if (pillRef.current && !pillRef.current.contains(e.target)) setExpanded(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [expanded]);

  const stale = useMemo(() => {
    if (!state?.updated_at) return true;
    return new Date(state.updated_at) < lastMondayMidnight();
  }, [state]);

  const totalSpent = useMemo(() => {
    if (!state) return 0;
    return (state.total_credits || 0) * (state.cost_per_credit_inr || 0);
  }, [state]);

  const liveRate = parseFloat(rate) || DEFAULT_RATE;
  const liveCredits = parseFloat(credits) || 0;
  const livePreview = liveCredits * liveRate;

  const trendChip = useMemo(() => {
    if (history.length < 2) return null;
    const last = history[history.length - 1].credits;
    const prev = history[history.length - 2].credits;
    const diff = last - prev;
    const pct = prev > 0 ? (diff / prev) * 100 : 0;
    return {
      diff,
      pct,
      up: diff > 0,
      down: diff < 0,
      label: `${diff > 0 ? '+' : ''}${compactNumber(diff)} cr (${diff > 0 ? '+' : ''}${pct.toFixed(0)}%)`,
    };
  }, [history]);

  const onOpenDialog = () => {
    setCredits(state?.total_credits != null ? String(state.total_credits) : '');
    setRate(state?.cost_per_credit_inr != null ? String(state.cost_per_credit_inr) : String(DEFAULT_RATE));
    setOpen(true);
  };

  const onSave = async () => {
    const c = parseFloat(credits);
    const r = parseFloat(rate);
    if (isNaN(c) || c < 0) { toast.error('Enter a valid credits value'); return; }
    if (isNaN(r) || r < 0) { toast.error('Enter a valid per-credit rate'); return; }
    setSaving(true);
    try {
      await axios.put(
        `${API}/cost-tracker`,
        { total_credits: c, cost_per_credit_inr: r },
        { headers: authHeader() }
      );
      await fetchAll();
      toast.success('Cost tracker updated');
      setOpen(false);
      setExpanded(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !state) return null;

  // Chart dataset with parsed Date for X-axis formatting
  const chartData = history.map(h => ({
    ...h,
    date: new Date(h.week_start + 'T00:00:00'),
  }));

  return (
    <>
      {/* Collapsed → icon, Expanded → stat-card pill */}
      {!expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          data-testid="cost-tracker-icon"
          className="relative inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-muted transition-colors"
          aria-label="Cost tracker"
        >
          <img
            src={RUPEE_ICON}
            alt=""
            className="h-7 w-7 object-contain hover:scale-110 transition-transform"
          />
          {stale && (
            <span
              data-testid="cost-tracker-stale-dot"
              className="absolute top-1.5 right-1.5 flex h-2.5 w-2.5"
            >
              <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500 border border-white" />
            </span>
          )}
        </button>
      ) : (
        <div
          ref={pillRef}
          data-testid="cost-tracker-pill"
          className="relative group h-11 rounded-xl border border-slate-200 bg-white flex items-stretch overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.05)] animate-in fade-in slide-in-from-right-2 duration-200 cursor-pointer"
          onClick={onOpenDialog}
          title="Click to edit"
        >
          {/* LEFT cell: credits */}
          <div className="flex items-center gap-2 px-3 bg-slate-50/50">
            <img src={RUPEE_ICON} alt="" className="h-4 w-4 object-contain" />
            <div className="flex flex-col leading-none">
              <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Credits</span>
              <span className="text-[13px] font-bold tabular-nums text-[#0F0F0F]" data-testid="cost-tracker-credits">
                {compactNumber(state?.total_credits || 0)}
              </span>
            </div>
          </div>
          {/* Divider */}
          <div className="w-px bg-slate-200" />
          {/* RIGHT cell: spent + sparkline */}
          <div className="flex items-center gap-2 px-3">
            <div className="flex flex-col leading-none">
              <span className="text-[9px] font-semibold uppercase tracking-wider text-[#2EB4AC]">Spent</span>
              <span className="text-[13px] font-bold tabular-nums text-[#0F0F0F]" data-testid="cost-tracker-spent">
                ₹{formatINR(totalSpent)}
              </span>
            </div>
            <Sparkline points={history} />
            {stale ? (
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500 border border-white" />
              </span>
            ) : (
              <Pencil size={12} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
            )}
          </div>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="cost-tracker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[17px]">
              <img src={RUPEE_ICON} alt="" className="h-5 w-5 object-contain" />
              App Development Cost Tracker
            </DialogTitle>
          </DialogHeader>

          {stale && (
            <div
              data-testid="cost-tracker-stale-alert"
              className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800"
            >
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>
                It’s a new week — please update the credits consumed since last Monday.
              </span>
            </div>
          )}

          {/* Trendline */}
          {chartData.length >= 2 ? (
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#0F0F0F]">Week-on-Week Credits</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Last {chartData.length} weeks · Mondays
                  </p>
                </div>
                {trendChip && (
                  <div
                    data-testid="cost-tracker-trend-chip"
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                      trendChip.up
                        ? 'bg-red-50 text-red-600'
                        : trendChip.down
                        ? 'bg-green-50 text-green-600'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {trendChip.up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {trendChip.label}
                  </div>
                )}
              </div>
              <div style={{ height: 176 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
                    <defs>
                      <linearGradient id="costTrackerFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2EB4AC" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#2EB4AC" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => format(d, 'd MMM')}
                      tick={{ fontSize: 10, fill: '#64748B' }}
                      axisLine={{ stroke: '#E2E8F0' }}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={compactNumber}
                      tick={{ fontSize: 10, fill: '#64748B' }}
                      axisLine={{ stroke: '#E2E8F0' }}
                      tickLine={false}
                      width={40}
                    />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }}
                      labelFormatter={(d) => format(d, 'd MMM yyyy')}
                      formatter={(value) => [compactNumber(value), 'Credits']}
                    />
                    <Area
                      type="monotone"
                      dataKey="credits"
                      stroke="#2EB4AC"
                      strokeWidth={2}
                      fill="url(#costTrackerFill)"
                      dot={{ r: 3, fill: '#fff', stroke: '#2EB4AC', strokeWidth: 1.6 }}
                      activeDot={{ r: 4, fill: '#2EB4AC' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div
              data-testid="cost-tracker-empty-state"
              className="rounded-lg border border-dashed border-slate-300 bg-slate-50/50 px-4 py-6 text-center text-[13px] text-slate-500"
            >
              Save updates each week to build the trendline.
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Credits consumed
              </Label>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
                placeholder="e.g. 1250"
                className="mt-1.5"
                data-testid="cost-tracker-credits-input"
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Per credit (₹)
              </Label>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="13.70"
                className="mt-1.5"
                data-testid="cost-tracker-rate-input"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Default ₹13.70 — edit anytime as billing changes.
              </p>
            </div>
          </div>

          <div className="rounded-md bg-slate-50/60 border border-slate-200 p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Total estimated spend
            </p>
            <p className="text-2xl font-bold text-[#0F0F0F] tabular-nums mt-1" data-testid="cost-tracker-live-preview">
              ₹{formatINR(livePreview)}
            </p>
          </div>

          {state?.updated_at && (
            <p className="text-[11px] text-slate-500" data-testid="cost-tracker-last-updated">
              Last updated {format(new Date(state.updated_at), 'PPp')} by {emailLocalpart(state.updated_by)}
            </p>
          )}

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="cost-tracker-cancel">
              Cancel
            </Button>
            <Button
              onClick={onSave}
              disabled={saving}
              data-testid="cost-tracker-save"
              className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
